import {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useQueryClient} from 'react-query';
import {ColumnDef} from '@tanstack/react-table';
import {Alert, Card, Col, Form, OverlayTrigger, ProgressBar, Row, Tooltip} from 'react-bootstrap';
import {saveAs} from 'file-saver';
import jsPDF from 'jspdf';
import {IntlShape, useIntl} from 'react-intl';
import TableMetronic from '../../../../../app/common/components/table/TableMetronic';
import Level3Title from '../../../../../app/common/components/title/Level3Title';
import ConnexionDot from '../../ConnexionDot';
import ActionIcon from '../../../../../Components/ActionIcon';
import Button from '../../../../../Components/Button';
import TabComponent from '../../../../../app/common/components/TabComponent';
import {formatBytes} from '../../../../../app/common/tools/filesize';
import {ApiVoicemailGreeting} from '../../../Api/telephonie';
import {InfoRow, InfoRows} from './common';
import {
  IdentifiantsSip,
  LocalisationSite,
  NumeroDetail,
  StatutBoiteVocale,
  TrunkNumero,
} from './mocks';
import {ZONE_LABEL_ID} from './RestrictionFiltrageIp';
import {
  useGetAuthorizationOverrides,
  useGetBlacklistEntrants,
  useGetIpRestriction,
  useGetLocalisationsUrgence,
  useGetNumberIpRestriction,
  useGetNumeroDetail,
  useGetNumeroPassword,
  useGetTrunkNumeros,
  useGetVirtualNumbers,
  useGetVoicemailGreeting,
  useMettreAJourNumero,
  useUpdateVoicemailGreeting,
} from '../../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../../Hooks/useAutoDismissMessage';
import Loading from '../../../../../app/common/components/Loading';

const NUMERO_COURANT = 'Numéro courant';
const NUMERO_MASQUE = 'Masqué';

/** Vert/rouge repris de la convention de couleurs d'état déjà utilisée (MyBills, ConnexionDot). */
const COULEUR_ACTIF = '#5da170';
const COULEUR_INACTIF = '#c44d4d';

type TypeNumero = 'principal' | 'lie' | 'reserve';

const typeDeNumero = (numero: TrunkNumero): TypeNumero | null => {
  if (numero.principal) return 'principal';
  if (numero.lie) return 'lie';
  if (numero.reserve) return 'reserve';
  return null;
};

const ICONE_TYPE_NUMERO: Record<TypeNumero, {classe: string; labelId: string}> = {
  principal: {classe: 'bi-star-fill text-warning', labelId: 'TELEPHONIE.NUMEROS.TYPE.PRINCIPAL'},
  lie: {classe: 'bi-link-45deg text-info', labelId: 'TELEPHONIE.NUMEROS.TYPE.LIE'},
  reserve: {classe: 'bi-archive-fill text-secondary', labelId: 'TELEPHONIE.NUMEROS.TYPE.RESERVE'},
};

type Vue = 'liste' | 'consulter' | 'modifier' | 'modifier-groupe';
type OngletModifier = 'boite-vocale' | 'appels-entrants' | 'appels-sortants' | 'params-avances';
type OngletModifierGroupe = 'boite-vocale' | 'appels-entrants' | 'appels-sortants';

interface RedirectionValeur {
  activee: boolean;
  mode: 'boite-vocale' | 'numero';
  numero: string;
  delaiSecondes?: number;
}

const ligneEnRedirectionValeur = (ligne: InfoRow | undefined, delaiSecondes?: number): RedirectionValeur => {
  if (!ligne || ligne.value === 'Non') {
    return {activee: false, mode: 'boite-vocale', numero: '', delaiSecondes};
  }
  if (ligne.value === 'Boîte vocale') {
    return {activee: true, mode: 'boite-vocale', numero: '', delaiSecondes};
  }
  return {activee: true, mode: 'numero', numero: ligne.value, delaiSecondes};
};

interface SonnerieParalleleValeur {
  activee: boolean;
  numero: string;
}

interface OptionsAppelsEntrantsValeurs {
  sonnerieParallele: SonnerieParalleleValeur;
  miseEnAttente: boolean;
  rejetAnonymes: boolean;
  protectionRenvois: boolean;
  signalAppel: boolean;
}

interface AppelsEntrantsValeurs extends OptionsAppelsEntrantsValeurs {
  absence: RedirectionValeur;
  occupe: RedirectionValeur;
  inconditionnelle: RedirectionValeur;
  backup: RedirectionValeur;
}

const redirectionValeurEnRedirect = (
  valeur: RedirectionValeur
): {enabled: boolean; target: string} => ({
  enabled: valeur.activee,
  // Désactivée : on vide la cible, sinon VTX la conserve et la redirection reste active.
  target: !valeur.activee ? '' : valeur.mode === 'boite-vocale' ? 'voicemail' : valeur.numero,
});

/**
 * Mirrors the backend check in `UpdateServiceNumber::requirePhoneNumber` (api-telephonie) :
 * an active redirect in "Numéro" mode needs a non-empty target matching `^\+?\d+$`,
 * otherwise the API rejects the whole update with a 422.
 */
const validerRedirection = (valeur: RedirectionValeur): string | undefined => {
  if (!valeur.activee || valeur.mode !== 'numero') return undefined;
  if (valeur.numero.trim() === '') return 'TELEPHONIE.NUMEROS.REDIRECTION.NUMERO_REQUIS';
  if (!/^\+?\d+$/.test(valeur.numero.trim())) return 'TELEPHONIE.NUMEROS.REDIRECTION.NUMERO_INVALIDE';
  return undefined;
};

/** Construit le payload `inboundCalls` de `PUT .../number/{numeroId}` à partir des redirections saisies. */
const appelsEntrantsEnInboundCallsPayload = ({
  absence,
  occupe,
  inconditionnelle,
  backup,
  sonnerieParallele,
  miseEnAttente,
  rejetAnonymes,
  protectionRenvois,
  signalAppel,
}: AppelsEntrantsValeurs) => ({
  unavailableRedirect: redirectionValeurEnRedirect(absence),
  noAnswerTimeout: absence.delaiSecondes,
  busyRedirect: redirectionValeurEnRedirect(occupe),
  unconditionalRedirect: redirectionValeurEnRedirect(inconditionnelle),
  // backupEnabled=false demande explicitement l'effacement (un backupNumber absent laisse la valeur existante).
  backupEnabled: backup.activee,
  backupNumber: backup.activee ? redirectionValeurEnRedirect(backup).target : undefined,
  parallelRingingEnabled: sonnerieParallele.activee,
  // Désactivée : on vide le numéro, sinon VTX le conserve et la sonnerie en parallèle reste active.
  parallelRingingNumber: sonnerieParallele.activee ? sonnerieParallele.numero.trim() : '',
  musicOnHold: miseEnAttente,
  rejectAnonymousCalls: rejetAnonymes,
  rejectForwardedCalls: protectionRenvois,
  callWaiting: signalAppel,
});

/** Même contrôle que `validerRedirection` : une sonnerie en parallèle active exige un numéro `^\+?\d+$`. */
const validerSonnerieParallele = (valeur: SonnerieParalleleValeur): string | undefined => {
  if (!valeur.activee) return undefined;
  if (valeur.numero.trim() === '') return 'TELEPHONIE.NUMEROS.REDIRECTION.NUMERO_REQUIS';
  if (!/^\+?\d+$/.test(valeur.numero.trim())) return 'TELEPHONIE.NUMEROS.REDIRECTION.NUMERO_INVALIDE';
  return undefined;
};

const identifiantsSipEnLignes = (
  identifiants: IdentifiantsSip,
  intl: IntlShape,
  onVoirMotDePasse: () => void,
  isPabx: boolean
): InfoRow[] => [
  {label: intl.formatMessage({id: 'AUTH.INPUT.USERNAME'}), value: identifiants.nomUtilisateur},
  ...(isPabx
    ? []
    : [
        {
          label: intl.formatMessage({id: 'AUTH.INPUT.PASSWORD'}),
          value: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.VOIR'}),
          link: true,
          onClick: onVoirMotDePasse,
        },
      ]),
  {
    label: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.SERVEUR_SIP'}),
    value: identifiants.serveurSip,
  },
  {
    label: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.PROXY_SIP'}),
    value: identifiants.proxySip,
  },
  {label: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.IDENTIFIANTS_SIP.NPA'}), value: identifiants.npa},
];

const LABEL_BOITE_VOCALE_LANGUE = 'Langue';
const LABEL_BOITE_VOCALE_MOT_DE_PASSE = 'Mot de passe';

/**
 * Lignes de la carte « Boîte vocale » de la vue consultation. Le code est renvoyé en clair
 * par l'API (`detail.voicemail.password`) : « Voir » l'affiche sans nouvel appel. Sur
 * Essentielle, la langue n'est pas appliquée par la plateforme : elle n'est pas affichée.
 */
const boiteVocaleEnLignes = (
  numero: TrunkNumero,
  isEssentielle: boolean,
  codeVisible: boolean,
  onVoirCode: () => void
): InfoRow[] =>
  numero.boiteVocale
    .filter((ligne) => !(isEssentielle && ligne.label === LABEL_BOITE_VOCALE_LANGUE))
    .map((ligne) => {
      const code = numero.detail?.voicemail?.password;
      if (ligne.label !== LABEL_BOITE_VOCALE_MOT_DE_PASSE || !code) return ligne;
      return codeVisible ? {label: ligne.label, value: code} : {...ligne, onClick: onVoirCode};
    });

const numeroAfficheLabel = (numero: TrunkNumero, intl: IntlShape): string => {
  if (!numero.numeroAffiche || numero.numeroAffiche === NUMERO_COURANT) {
    return numero.numero;
  }
  return numero.numeroAffiche === NUMERO_MASQUE
    ? intl.formatMessage({id: 'TELEPHONIE.NUMEROS.NUMERO_MASQUE_LABEL'})
    : numero.numeroAffiche;
};

/** Ordre d'affichage par défaut : le numéro principal, puis les numéros liés, puis le reste, les réservés en dernier. */
const prioriteAffichage = (numero: TrunkNumero): number => {
  if (numero.principal) return 0;
  if (numero.lie) return 1;
  if (numero.reserve) return 3;
  return 2;
};

const parOrdreAffichage = (a: TrunkNumero, b: TrunkNumero): number =>
  prioriteAffichage(a) - prioriteAffichage(b);

const InfoCard = ({
  title,
  rows,
  action,
  onActionClick,
  printAction,
  onPrintClick,
}: {
  title: string;
  rows: InfoRow[];
  action?: string;
  onActionClick?: () => void;
  printAction?: string;
  onPrintClick?: () => void;
}): JSX.Element => (
  <Card className="mb-5">
    <Card.Header className="d-flex align-items-center justify-content-between">
      <Card.Title className="m-0">
        <Level3Title title={title} />
      </Card.Title>
      {(printAction || action) && (
        <span className="d-flex align-items-center gap-2">
          {printAction && (
            <span className="text-primary fs-6 cursor-pointer" onClick={onPrintClick}>
              {printAction}
            </span>
          )}
          {printAction && action && <span className="text-muted">·</span>}
          {action && (
            <span className="text-primary fs-6 cursor-pointer" onClick={onActionClick}>
              {action}
            </span>
          )}
        </span>
      )}
    </Card.Header>
    <Card.Body>
      <InfoRows rows={rows} />
    </Card.Body>
  </Card>
);

const ExportLink = ({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick: () => void;
}): JSX.Element => (
  <span className="d-flex align-items-center gap-2 fw-semibold cursor-pointer" role="button" onClick={onClick}>
    <i className={`bi ${icon} fs-2`} />
    {label}
  </span>
);

const RedirectionSection = ({
  title,
  name,
  valeur,
  onChange,
  voicemailActive = false,
  estBackup = false,
  erreur,
  disabled = false,
}: {
  title: string;
  name: string;
  valeur: RedirectionValeur;
  onChange: (valeur: RedirectionValeur) => void;
  voicemailActive?: boolean;
  estBackup?: boolean;
  erreur?: string;
  /** Verrouille tous les champs de la section, le temps d'une soumission en cours. */
  disabled?: boolean;
}): JSX.Element => {
  const intl = useIntl();

  return (
    <Card className="mb-4">
      <Card.Body>
        <div className="row g-4">
          <div className="col-12 col-md-5">
            <Form.Check
              type="checkbox"
              id={`check-${name}`}
              label={title}
              checked={valeur.activee}
              disabled={disabled}
              onChange={(e) => onChange({...valeur, activee: e.target.checked})}
            />
          </div>
          <div className="col-12 col-md-7">
            <Form.Check
              type="radio"
              name={name}
              id={`${name}-vocale`}
              checked={valeur.mode === 'boite-vocale'}
              disabled={disabled || !valeur.activee}
              onChange={() => onChange({...valeur, mode: 'boite-vocale'})}
              className="mb-2"
              label={
                <>
                  {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.REDIRECTION.MODE_BOITE_VOCALE'})}{' '}
                  {!voicemailActive && (
                    <span className="text-danger fs-8">
                      {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.REDIRECTION.BOITE_VOCALE_INACTIVE'})}
                    </span>
                  )}
                </>
              }
            />
            <div className="d-flex align-items-center gap-3">
              <Form.Check
                type="radio"
                name={name}
                id={`${name}-numero`}
                checked={valeur.mode === 'numero'}
                disabled={disabled || !valeur.activee}
                onChange={() => onChange({...valeur, mode: 'numero'})}
                label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.REDIRECTION.MODE_NUMERO'})}
              />
              <Form.Control
                type="text"
                value={valeur.numero}
                disabled={disabled || !valeur.activee || valeur.mode !== 'numero'}
                onChange={(e) => onChange({...valeur, numero: e.target.value})}
                isInvalid={!!erreur}
                style={{maxWidth: 180}}
                size="sm"
              />
              {valeur.delaiSecondes !== undefined && (
                <span className="text-muted fs-7">
                  {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.REDIRECTION.APRES'})}{' '}
                  <Form.Control
                    type="number"
                    value={valeur.delaiSecondes}
                    disabled={disabled || !valeur.activee}
                    onChange={(e) => onChange({...valeur, delaiSecondes: Number(e.target.value)})}
                    style={{width: 70, display: 'inline-block'}}
                    size="sm"
                  />{' '}
                  {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.REDIRECTION.SECONDES'})}
                </span>
              )}
            </div>
            {erreur && <div className="invalid-feedback d-block">{intl.formatMessage({id: erreur})}</div>}
            {estBackup && (
              <div className="text-muted fs-8 mt-2">
                {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.REDIRECTION.BACKUP_INFO'})}{' '}
                <strong>{intl.formatMessage({id: 'TELEPHONIE.NUMEROS.REDIRECTION.BACKUP_INFO_STRONG'})}</strong>.
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const OptionAppelEntrantSection = ({
  id,
  title,
  aide,
  checked,
  onChange,
  disabled = false,
  children,
}: {
  id: string;
  title: string;
  /** Texte de l'infobulle « ? » affichée à côté du libellé. */
  aide: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  children?: ReactNode;
}): JSX.Element => (
  <Card className="mb-4">
    <Card.Body>
      <div className="row g-4">
        <div className="col-12 col-md-5">
          <Form.Check
            type="checkbox"
            id={id}
            label={
              <>
                {title}{' '}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`${id}-aide`}>
                      <span style={{whiteSpace: 'pre-line'}}>{aide}</span>
                    </Tooltip>
                  }
                >
                  <i className="bi bi-question-circle-fill text-primary cursor-pointer" />
                </OverlayTrigger>
              </>
            }
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
        </div>
        <div className="col-12 col-md-7">{children}</div>
      </div>
    </Card.Body>
  </Card>
);

const LABEL_ABSENCE = "Redirection en cas d'absence";
const LABEL_OCCUPE = 'Redirection si occupé';
const LABEL_INCONDITIONNELLE = 'Redirection inconditionnelle';
const LABEL_BACKUP = 'Redirection de Backup';

const ModifierAppelsEntrants = ({
  appelsEntrants,
  voicemailActive = false,
  noAnswerTimeout,
  parametres,
  onConfirm,
  isSubmitting = false,
}: {
  appelsEntrants: InfoRow[];
  voicemailActive?: boolean;
  noAnswerTimeout?: number;
  /** Réglages courants du numéro (`detail.inboundCalls`) : valeurs initiales des options sous les redirections. */
  parametres?: NumeroDetail['inboundCalls'];
  onConfirm: (valeurs: AppelsEntrantsValeurs) => void;
  /** Soumission en cours (`mettreAJourNumeroApi.isLoading`) : verrouille le formulaire et affiche un loader sur « Confirmer ». */
  isSubmitting?: boolean;
}): JSX.Element => {
  const intl = useIntl();
  const trouver = (label: string): InfoRow | undefined =>
    appelsEntrants.find((ligne) => ligne.label === label);

  const [absence, setAbsence] = useState<RedirectionValeur>(
    ligneEnRedirectionValeur(trouver(LABEL_ABSENCE), noAnswerTimeout ?? 30)
  );
  const [occupe, setOccupe] = useState<RedirectionValeur>(ligneEnRedirectionValeur(trouver(LABEL_OCCUPE)));
  const [inconditionnelle, setInconditionnelle] = useState<RedirectionValeur>(
    ligneEnRedirectionValeur(trouver(LABEL_INCONDITIONNELLE))
  );
  const [backup, setBackup] = useState<RedirectionValeur>(ligneEnRedirectionValeur(trouver(LABEL_BACKUP)));
  const [sonnerieParallele, setSonnerieParallele] = useState<SonnerieParalleleValeur>({
    activee: parametres?.parallelRingingEnabled ?? false,
    numero: parametres?.parallelRingingNumber ?? '',
  });
  const [miseEnAttente, setMiseEnAttente] = useState(parametres?.musicOnHold ?? false);
  const [rejetAnonymes, setRejetAnonymes] = useState(parametres?.rejectAnonymousCalls ?? false);
  const [protectionRenvois, setProtectionRenvois] = useState(parametres?.rejectForwardedCalls ?? false);
  const [signalAppel, setSignalAppel] = useState(parametres?.callWaiting ?? false);
  const [afficherErreurs, setAfficherErreurs] = useState(false);

  const erreurs = useMemo(
    () => ({
      absence: validerRedirection(absence),
      occupe: validerRedirection(occupe),
      inconditionnelle: validerRedirection(inconditionnelle),
      backup: validerRedirection(backup),
      sonnerieParallele: validerSonnerieParallele(sonnerieParallele),
    }),
    [absence, occupe, inconditionnelle, backup, sonnerieParallele]
  );

  const confirmer = (): void => {
    if (Object.values(erreurs).some((erreur) => erreur !== undefined)) {
      setAfficherErreurs(true);
      return;
    }
    onConfirm({
      absence,
      occupe,
      inconditionnelle,
      backup,
      sonnerieParallele,
      miseEnAttente,
      rejetAnonymes,
      protectionRenvois,
      signalAppel,
    });
  };

  return (
    <div className="mt-4">
      <p className="text-muted fs-6 mb-5">
        {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.APPELS_ENTRANTS.INTRO'})}
      </p>
      <RedirectionSection
        title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.REDIRECTION.ABSENCE'})}
        name="absence"
        valeur={absence}
        onChange={setAbsence}
        voicemailActive={voicemailActive}
        erreur={afficherErreurs ? erreurs.absence : undefined}
        disabled={isSubmitting}
      />
      <RedirectionSection
        title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.REDIRECTION.OCCUPE'})}
        name="occupe"
        valeur={occupe}
        onChange={setOccupe}
        voicemailActive={voicemailActive}
        erreur={afficherErreurs ? erreurs.occupe : undefined}
        disabled={isSubmitting}
      />
      <RedirectionSection
        title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.REDIRECTION.INCONDITIONNELLE'})}
        name="inconditionnelle"
        valeur={inconditionnelle}
        onChange={setInconditionnelle}
        voicemailActive={voicemailActive}
        erreur={afficherErreurs ? erreurs.inconditionnelle : undefined}
        disabled={isSubmitting}
      />
      <RedirectionSection
        title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.REDIRECTION.BACKUP'})}
        name="backup"
        valeur={backup}
        onChange={setBackup}
        voicemailActive={voicemailActive}
        erreur={afficherErreurs ? erreurs.backup : undefined}
        estBackup
        disabled={isSubmitting}
      />
      <OptionAppelEntrantSection
        id="check-sonnerie-parallele"
        title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.OPTION.SONNERIE_PARALLELE'})}
        aide={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.OPTION.SONNERIE_PARALLELE_AIDE'})}
        checked={sonnerieParallele.activee}
        onChange={(activee) => setSonnerieParallele({...sonnerieParallele, activee})}
        disabled={isSubmitting}
      >
        <Form.Control
          type="text"
          value={sonnerieParallele.numero}
          disabled={isSubmitting || !sonnerieParallele.activee}
          onChange={(e) => setSonnerieParallele({...sonnerieParallele, numero: e.target.value})}
          isInvalid={afficherErreurs && !!erreurs.sonnerieParallele}
          style={{maxWidth: 240}}
          size="sm"
        />
        <div className="text-muted fs-8 mt-1">
          {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.OPTION.SONNERIE_PARALLELE_EXEMPLE'})}
        </div>
        {afficherErreurs && erreurs.sonnerieParallele && (
          <div className="invalid-feedback d-block">{intl.formatMessage({id: erreurs.sonnerieParallele})}</div>
        )}
      </OptionAppelEntrantSection>
      <OptionAppelEntrantSection
        id="check-mise-en-attente"
        title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.OPTION.MISE_EN_ATTENTE'})}
        aide={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.OPTION.MISE_EN_ATTENTE_AIDE'})}
        checked={miseEnAttente}
        onChange={setMiseEnAttente}
        disabled={isSubmitting}
      />
      <OptionAppelEntrantSection
        id="check-rejet-anonymes"
        title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.OPTION.REJET_ANONYMES'})}
        aide={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.OPTION.REJET_ANONYMES_AIDE'})}
        checked={rejetAnonymes}
        onChange={setRejetAnonymes}
        disabled={isSubmitting}
      />
      <OptionAppelEntrantSection
        id="check-protection-renvois"
        title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.OPTION.PROTECTION_RENVOIS'})}
        aide={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.OPTION.PROTECTION_RENVOIS_AIDE'})}
        checked={protectionRenvois}
        onChange={setProtectionRenvois}
        disabled={isSubmitting}
      />
      <OptionAppelEntrantSection
        id="check-signal-appel"
        title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.OPTION.SIGNAL_APPEL'})}
        aide={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.OPTION.SIGNAL_APPEL_AIDE'})}
        checked={signalAppel}
        onChange={setSignalAppel}
        disabled={isSubmitting}
      />
      <div className="text-center">
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CONFIRMER'})}
          onClick={confirmer}
          loading={isSubmitting}
        />
      </div>
    </div>
  );
};

/** Construit le payload `outboundCalls` de `PUT .../number/{numeroId}` à partir du choix de numéro affiché. */
const numeroAfficheEnOutboundCallsPayload = (
  numeroAffiche: string
): {callerIdHidden: boolean; callerIdNumber?: string} => {
  if (numeroAffiche === NUMERO_MASQUE) {
    return {callerIdHidden: true};
  }
  if (numeroAffiche === NUMERO_COURANT) {
    return {callerIdHidden: false};
  }
  return {callerIdHidden: false, callerIdNumber: numeroAffiche};
};

const ModifierAppelsSortants = ({
  autresNumeros,
  valeurInitiale,
  onConfirm,
  isSubmitting = false,
}: {
  autresNumeros: string[];
  valeurInitiale: string;
  onConfirm: (numeroAffiche: string) => void;
  /** Soumission en cours (`mettreAJourNumeroApi.isLoading`) : verrouille le formulaire et affiche un loader sur « Confirmer ». */
  isSubmitting?: boolean;
}): JSX.Element => {
  const intl = useIntl();
  const [numeroAffiche, setNumeroAffiche] = useState<string>(valeurInitiale);

  return (
    <div className="mt-4">
      <p className="text-muted fs-6 mb-5">
        {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.APPELS_SORTANTS.INTRO'})}
      </p>
      <Card className="mb-5">
        <Card.Body className="d-flex align-items-center gap-3">
          <Form.Label className="fs-6 fw-semibold m-0">
            {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.VOIR'})}
          </Form.Label>
          <Form.Select
            style={{maxWidth: 260}}
            value={numeroAffiche}
            onChange={(e) => setNumeroAffiche(e.target.value)}
            disabled={isSubmitting}
          >
            <option value={NUMERO_COURANT}>
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.AFFICHER_NUMERO_COURANT'})}
            </option>
            <option value={NUMERO_MASQUE}>
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.MASQUER_NUMERO'})}
            </option>
            {autresNumeros.map((numero) => (
              <option key={numero} value={numero}>
                {numero}
              </option>
            ))}
          </Form.Select>
        </Card.Body>
      </Card>
      <div className="text-center">
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CONFIRMER'})}
          onClick={() => onConfirm(numeroAffiche)}
          loading={isSubmitting}
        />
      </div>
    </div>
  );
};

const EMAIL_REGEX = /^[A-Z0-9._%-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

/** Texte (bleu) résumant l'état courant, à côté des radios — distinct de l'annonce/enregistrement. */
const ETAT_ANNONCE_ID: Record<StatutBoiteVocale, string> = {
  actif: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ETAT_ACTIVE',
  annonce_seule: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ETAT_ACTIVE',
  inactif: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ETAT_INACTIVE',
};

const ETAT_ENREGISTREMENT_ID: Record<StatutBoiteVocale, string> = {
  actif: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ETAT_ACTIF',
  annonce_seule: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ETAT_INACTIF',
  inactif: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ETAT_INACTIF',
};

const ANNONCE_SUFFIXE_ID: Record<StatutBoiteVocale, string> = {
  actif: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ANNONCE_SUFFIXE_ACTIVE',
  annonce_seule: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ANNONCE_SUFFIXE_ANNONCE_SEULE',
  inactif: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ANNONCE_SUFFIXE_DESACTIVEE',
};

/**
 * `Voip_Update_NumberOptions` (api-telephonie) a un comportement confirmé
 * incohérent face à un paramètre positionnel omis/null — certains sont
 * préservés, d'autres remis à zéro (cf. @ShowName, qui vide CLID_NUMBER si
 * @ShowNumber est renseigné sans lui). Paramètres avancés (langue) et Boîte
 * vocale (statut/email/conservation) possèdent chacun une partie de
 * `voicemail` : plutôt que de supposer que l'autre partie survit à un envoi
 * partiel, chaque écran renvoie systématiquement les champs qu'il ne modifie
 * pas tels quels.
 */
const voicemailInchange = (
  voicemail: NumeroDetail['voicemail']
): {
  enabled?: boolean;
  announcementOnly?: boolean;
  keepOnServer?: boolean;
  email?: string;
  language?: string;
} => ({
  enabled: voicemail?.enabled ?? undefined,
  announcementOnly: voicemail?.announcementOnly ?? undefined,
  keepOnServer: voicemail?.keepOnServer ?? undefined,
  email: voicemail?.email ?? undefined,
  language: voicemail?.language ?? undefined,
});

/** `enabled`/`announcementOnly` (api-telephonie) combinés dans le même 3-états que la maquette. */
const statutDepuisVoicemail = (voicemail: NumeroDetail['voicemail']): StatutBoiteVocale => {
  if (!voicemail?.enabled) {
    return 'inactif';
  }
  return voicemail.announcementOnly ? 'annonce_seule' : 'actif';
};

const voicemailDepuisStatut = (
  statut: StatutBoiteVocale
): {enabled: boolean; announcementOnly: boolean} =>
  statut === 'inactif'
    ? {enabled: false, announcementOnly: false}
    : {enabled: true, announcementOnly: statut === 'annonce_seule'};

/** api-telephonie refuse de désactiver une boîte vocale encore utilisée (409 `/errors/voicemail-in-use`). */
const estBoiteVocaleUtilisee = (erreur: unknown): boolean =>
  (erreur as {response?: {data?: {type?: string}}} | undefined)?.response?.data?.type ===
  '/errors/voicemail-in-use';

/**
 * `audioContent` attendu par api-telephonie : le base64 seul, sans le
 * préfixe `data:...;base64,`.
 */
const fichierEnBase64 = (fichier: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const lecteur = new FileReader();
    lecteur.onload = () => {
      const resultat = lecteur.result as string;
      resolve(resultat.slice(resultat.indexOf(',') + 1));
    };
    lecteur.onerror = () => reject(lecteur.error);
    lecteur.readAsDataURL(fichier);
  });

/** Texte affiché dans l'encart d'upload tant qu'aucun fichier n'est choisi localement. */
const annoncePlaceholder = ({
  fichier,
  greetingLoading,
  greeting,
  intl,
}: {
  fichier: File | null;
  greetingLoading: boolean;
  greeting: ApiVoicemailGreeting | undefined;
  intl: IntlShape;
}): string => {
  if (fichier) {
    return `${fichier.name}, ${formatBytes(fichier.size)}`;
  }
  if (greetingLoading) {
    return '…';
  }
  if (greeting?.audioContent) {
    return intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ANNONCE_ENREGISTREE'});
  }
  return intl.formatMessage({id: 'INPUT.FILE.PLACEHOLDER'});
};

interface ModifierBoiteVocaleProps {
  serviceId: number;
  numeroId: number;
  voicemail: NumeroDetail['voicemail'];
  onConfirm: (misAJour: TrunkNumero) => void;
}

/**
 * Sous-onglet « Boîte vocale » de la vue « Modifier un numéro » (Essentielle) —
 * reproduit VoiceMail.aspx, scopé au numéro déjà sélectionné (pas de sélecteur de
 * numéro ni de langue, contrairement à `Centrex/BoiteVocale`, qui reste la page
 * PBX globale, toujours mockée). Statut/email/conservation/code d'accès sont
 * persistés via `PUT .../number/{numeroId}` (même ressource que Paramètres
 * avancés/Appels entrants, cf. `voicemail` sur `NumeroDetail`) ; l'annonce
 * personnalisable est une ressource api-telephonie séparée
 * (`.../voicemail/greeting`, en base64) avec son propre cycle lecture/écriture.
 */
const ModifierBoiteVocale = ({
  serviceId,
  numeroId,
  voicemail,
  onConfirm,
}: ModifierBoiteVocaleProps): JSX.Element => {
  const intl = useIntl();
  const mettreAJourNumeroApi = useMettreAJourNumero(serviceId);
  const {data: greeting, isLoading: greetingLoading} = useGetVoicemailGreeting(
    serviceId,
    numeroId
  );
  const updateGreetingApi = useUpdateVoicemailGreeting(serviceId, numeroId);
  const [statut, setStatut] = useState<StatutBoiteVocale>(statutDepuisVoicemail(voicemail));
  const [fichier, setFichier] = useState<File | null>(null);
  const [email, setEmail] = useState(voicemail?.email ?? '');
  const [conserverMessages, setConserverMessages] = useState(voicemail?.keepOnServer ?? true);
  const [nouveauMotDePasse, setNouveauMotDePasse] = useState('');
  const [confirmationMotDePasse, setConfirmationMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | undefined>(undefined);
  const fichierInputRef = useRef<HTMLInputElement | null>(null);

  const decocherConserver = (coche: boolean): void => {
    if (!coche) {
      const confirme = window.confirm(
        intl.formatMessage({id: 'TELEPHONIE.PBX.BV.CONFIRMER_SUPPRESSION_AUTO'})
      );
      if (!confirme) {
        return;
      }
    }
    setConserverMessages(coche);
  };

  const confirmer = (): void => {
    if (email.trim() !== '' && !EMAIL_REGEX.test(email.trim())) {
      setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.BV.ERREUR_EMAIL'}));
      return;
    }
    if (!conserverMessages && email.trim() === '') {
      setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.BV.ERREUR_EMAIL_OBLIGATOIRE'}));
      return;
    }
    if (nouveauMotDePasse !== '' || confirmationMotDePasse !== '') {
      if (!/^\d{4}$/.test(nouveauMotDePasse)) {
        setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.BV.ERREUR_MOT_DE_PASSE_FORMAT'}));
        return;
      }
      if (nouveauMotDePasse !== confirmationMotDePasse) {
        setErreur(intl.formatMessage({id: 'TELEPHONIE.PBX.BV.ERREUR_MOT_DE_PASSE_CONFIRMATION'}));
        return;
      }
    }
    setErreur(undefined);

    const echec = (erreurApi?: unknown): void =>
      setErreur(
        intl.formatMessage({
          id: estBoiteVocaleUtilisee(erreurApi)
            ? 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ERREUR_UTILISEE'
            : 'TELEPHONIE.NUMEROS.ERREUR_MODIFICATION',
        })
      );

    const soumettre = async (): Promise<void> => {
      if (fichier) {
        const audioContent = await fichierEnBase64(fichier);
        await updateGreetingApi.mutateAsync(audioContent);
      }
      mettreAJourNumeroApi.mutate(
        {
          numeroId,
          payload: {
            voicemail: {
              ...voicemailInchange(voicemail),
              ...voicemailDepuisStatut(statut),
              keepOnServer: conserverMessages,
              email: email.trim(),
              ...(nouveauMotDePasse ? {password: nouveauMotDePasse} : {}),
            },
          },
        },
        {
          onSuccess: (misAJour) => {
            setNouveauMotDePasse('');
            setConfirmationMotDePasse('');
            setFichier(null);
            onConfirm(misAJour);
          },
          onError: echec,
        }
      );
    };

    soumettre().catch(echec);
  };

  const isSubmitting = mettreAJourNumeroApi.isLoading || updateGreetingApi.isLoading;

  return (
    <div className="mt-4">
      <p className="text-muted fs-6 mb-5">
        {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.INTRO'})}
      </p>

      {erreur && (
        <Alert variant="danger" onClose={() => setErreur(undefined)} dismissible>
          {erreur}
        </Alert>
      )}

      <Card className="mb-5">
        <Card.Body>
          <div className="row g-4 align-items-center mb-4">
            <div className="col-12 col-md-4 fw-semibold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.STATUT_LABEL'})}
            </div>
            <div className="col-12 col-md-8">
              <div className="row g-4">
                <div className="col-auto d-flex flex-column gap-1">
                  <Form.Check
                    type="radio"
                    id="bv-statut-actif"
                    label={intl.formatMessage({
                      id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.STATUT_ACTIVEE',
                    })}
                    checked={statut === 'actif'}
                    onChange={() => setStatut('actif')}
                  />
                  <Form.Check
                    type="radio"
                    id="bv-statut-annonce"
                    label={intl.formatMessage({id: 'TELEPHONIE.PBX.BV.STATUT.ANNONCE_SEULE'})}
                    checked={statut === 'annonce_seule'}
                    onChange={() => setStatut('annonce_seule')}
                  />
                  <Form.Check
                    type="radio"
                    id="bv-statut-inactif"
                    label={intl.formatMessage({
                      id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.STATUT_DESACTIVEE',
                    })}
                    checked={statut === 'inactif'}
                    onChange={() => setStatut('inactif')}
                  />
                </div>
                <div className="col-auto">
                  <div className="bg-primary text-white rounded p-3 fs-7">
                    <div>
                      {intl.formatMessage({
                        id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.RESUME_ANNONCE_PREFIX',
                      })}{' '}
                      <strong>{intl.formatMessage({id: ETAT_ANNONCE_ID[statut]})}</strong>.
                    </div>
                    <div>
                      {intl.formatMessage({
                        id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.RESUME_ENREGISTREMENT_PREFIX',
                      })}{' '}
                      <strong>{intl.formatMessage({id: ETAT_ENREGISTREMENT_ID[statut]})}</strong>.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4 align-items-center mb-4">
            <div className="col-12 col-md-4 fw-semibold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ANNONCE_LABEL'})} —{' '}
              {intl.formatMessage({id: ANNONCE_SUFFIXE_ID[statut]})}
              <div className="text-muted fs-8 fw-normal mt-1">
                {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.ANNONCE_FORMATS'})}
              </div>
            </div>
            <div className="col-12 col-md-8">
              <Row
                className="rounded-lg bg-celeste-form-bg p-0 m-0 file-input flex-nowrap text-nowrap"
                style={{maxWidth: 420}}
                onClick={() => fichierInputRef.current?.click()}
              >
                <Col xs="auto" className="p-5 m-0 file-input-btn">
                  {intl.formatMessage({id: 'INPUT.FILE.BUTTON'})}
                </Col>
                <Col className="h-full d-flex align-items-center">
                  {annoncePlaceholder({fichier, greetingLoading, greeting, intl})}
                </Col>
              </Row>
              <input
                type="file"
                accept=".wav,.mp3"
                className="visually-hidden"
                ref={fichierInputRef}
                onChange={(e) => setFichier(e.target.files?.[0] ?? null)}
              />
              {!fichier && greeting?.audioContent && (
                <audio
                  className="mt-2 d-block"
                  controls
                  style={{maxWidth: 420, height: 32}}
                  src={`data:audio/wav;base64,${greeting.audioContent}`}
                />
              )}
            </div>
          </div>

          <div className="row g-4 align-items-center mb-4">
            <div className="col-12 col-md-4 fw-semibold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.EMAIL_LABEL'})}
            </div>
            <div className="col-12 col-md-8">
              <Form.Control
                name="voicemail-notification-email"
                autoComplete="off"
                style={{maxWidth: 320}}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="row g-4 align-items-center mb-4">
            <div className="col-12 col-md-4 fw-semibold fs-6 d-flex align-items-center gap-2">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.CONSERVER_LABEL'})}
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.CONSERVER_TOOLTIP'})}
                  </Tooltip>
                }
              >
                <i className="bi bi-question-circle-fill text-primary fs-6 cursor-pointer" />
              </OverlayTrigger>
            </div>
            <div className="col-12 col-md-8">
              <Form.Check
                type="checkbox"
                className="form-check"
                id="bv-conserver-messages"
                checked={conserverMessages}
                onChange={(e) => decocherConserver(e.target.checked)}
              />
            </div>
          </div>

          <div className="row g-4 align-items-center">
            <div className="col-12 col-md-4 fw-semibold fs-6 d-flex align-items-center gap-2">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.CODE_ACCES_LABEL'})}
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.CODE_ACCES_TOOLTIP'})}
                  </Tooltip>
                }
              >
                <i className="bi bi-question-circle-fill text-primary fs-6 cursor-pointer" />
              </OverlayTrigger>
            </div>
            <div className="col-12 col-md-8">
              <div className="row g-4">
                <div className="col-auto">
                  <Form.Label className="fs-6 mb-0">
                    {intl.formatMessage({id: 'TELEPHONIE.PBX.BV.NOUVEAU_MOT_DE_PASSE'})}
                  </Form.Label>
                  <div className="text-muted fs-8 mb-1">
                    {intl.formatMessage({
                      id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.MOT_DE_PASSE_FORMAT',
                    })}
                  </div>
                  {/* new-password : empêche le navigateur de remplir ce code d'accès avec un mot de passe enregistré. */}
                  <Form.Control
                    type="password"
                    name="voicemail-access-code"
                    autoComplete="new-password"
                    maxLength={4}
                    style={{maxWidth: 140}}
                    value={nouveauMotDePasse}
                    onChange={(e) => setNouveauMotDePasse(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
                <div className="col-auto">
                  <Form.Label className="fs-6 mb-0">
                    {intl.formatMessage({
                      id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE.CONFIRMATION_MOT_DE_PASSE',
                    })}
                  </Form.Label>
                  <div className="fs-8 mb-1">&nbsp;</div>
                  <Form.Control
                    type="password"
                    name="voicemail-access-code-confirmation"
                    autoComplete="new-password"
                    maxLength={4}
                    style={{maxWidth: 140}}
                    value={confirmationMotDePasse}
                    onChange={(e) => setConfirmationMotDePasse(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="text-center">
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CONFIRMER'})}
          onClick={confirmer}
          loading={isSubmitting}
        />
      </div>
    </div>
  );
};

/** Même format que l'ancien écran (« Pully | Avenue de Lavaux 101 | 1009 | Pully »). */
const localisationEnLibelle = (site: LocalisationSite): string =>
  [site.nom, `${site.rue} ${site.numero}`.trim(), site.cp, site.ville].filter(Boolean).join(' | ');

const MINUSCULES = 'abcdefghijklmnopqrstuvwxyz';
const MAJUSCULES = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const CHIFFRES = '0123456789';
const CARACTERES_SPECIAUX = '!@#$%^&*-_';

const caractereAleatoire = (jeu: string): string => jeu[Math.floor(Math.random() * jeu.length)];

/**
 * Longueur 12 avec au moins un caractère de chaque catégorie : atteint
 * toujours 100 sur `complexiteMotDePasse` (palier « Forte »), quel que soit
 * le tirage.
 */
const genererMotDePasseFort = (): string => {
  const caracteres = [
    caractereAleatoire(MINUSCULES),
    caractereAleatoire(MAJUSCULES),
    caractereAleatoire(CHIFFRES),
    caractereAleatoire(CARACTERES_SPECIAUX),
  ];
  const alphabet = MINUSCULES + MAJUSCULES + CHIFFRES + CARACTERES_SPECIAUX;
  while (caracteres.length < 12) {
    caracteres.push(caractereAleatoire(alphabet));
  }

  for (let i = caracteres.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [caracteres[i], caracteres[j]] = [caracteres[j], caracteres[i]];
  }

  return caracteres.join('');
};

/** Score simple (longueur + diversité des caractères), sur 100. */
const complexiteMotDePasse = (motDePasse: string): number => {
  let score = 0;
  if (motDePasse.length >= 8) score += 30;
  if (motDePasse.length >= 12) score += 10;
  if (/[a-z]/.test(motDePasse)) score += 15;
  if (/[A-Z]/.test(motDePasse)) score += 15;
  if (/[0-9]/.test(motDePasse)) score += 15;
  if (/[^a-zA-Z0-9]/.test(motDePasse)) score += 15;
  return Math.min(100, score);
};

const niveauComplexite = (
  score: number
): {labelId: string; variantBarre: string; classeTexte: string} => {
  if (score >= 80) {
    return {
      labelId: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.FORTE',
      variantBarre: 'success',
      classeTexte: 'text-success',
    };
  }
  if (score >= 60) {
    return {
      labelId: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.MOYENNE',
      variantBarre: 'info',
      classeTexte: 'text-info',
    };
  }
  return {
    labelId: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.FAIBLE',
    variantBarre: 'warning',
    classeTexte: 'text-danger',
  };
};

const LANGUES_VOICEMAIL: {code: string; labelId: string}[] = [
  {code: 'fr', labelId: 'TELEPHONIE.NUMEROS.LANGUE.FRANCAIS'},
  {code: 'de', labelId: 'TELEPHONIE.NUMEROS.LANGUE.DEUTSCH'},
  {code: 'it', labelId: 'TELEPHONIE.NUMEROS.LANGUE.ITALIANO'},
  {code: 'en', labelId: 'TELEPHONIE.NUMEROS.LANGUE.ENGLISH'},
];

const ModifierParametresAvances = ({
  serviceId,
  numeroId,
  identifiantsSip,
  localisations,
  localisationIdActuelle,
  langueActuelle,
  isPabx = false,
  onConfirm,
}: {
  serviceId: number;
  numeroId: number;
  identifiantsSip: IdentifiantsSip;
  localisations: LocalisationSite[];
  localisationIdActuelle: number | null;
  langueActuelle: string;
  /** Pas de compte SIP exposé côté client sur un PABX : masque mot de passe/complexité/alerte. */
  isPabx?: boolean;
  onConfirm: (motDePasse: string, localisationId: number | null, langue: string) => void;
}): JSX.Element => {
  const intl = useIntl();
  const [motDePasse, setMotDePasse] = useState(identifiantsSip.motDePasse);
  const [localisationId, setLocalisationId] = useState<number | null>(localisationIdActuelle);
  const [langue, setLangue] = useState(langueActuelle);
  const [motDePasseVisible, setMotDePasseVisible] = useState(false);
  const numeroPassword = useGetNumeroPassword();

  useEffect(() => {
    if (isPabx) {
      return;
    }
    numeroPassword.mutate(
      {serviceId, numeroId, target: 'sip'},
      {onSuccess: (motDePasseActuel) => setMotDePasse(motDePasseActuel)}
    );
    // Révélation à la demande, une seule fois à l'ouverture du formulaire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, numeroId, isPabx]);

  const genererMotDePasse = (): void => {
    setMotDePasse(genererMotDePasseFort());
  };

  const complexite = complexiteMotDePasse(motDePasse);
  const niveau = niveauComplexite(complexite);

  return (
    <div className="mt-4">
      <p className="text-muted fs-6 mb-5">
        {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.INTRO'})}
      </p>
      <Card className="mb-5">
        <Card.Body>
          <div className="row g-4 align-items-center mb-4">
            <div className="col-12 col-md-4 fw-semibold fs-6">
              {intl.formatMessage({id: 'AUTH.INPUT.USERNAME'})} *
            </div>
            <div className="col-12 col-md-8 fs-6">{identifiantsSip.nomUtilisateur}</div>
            {!isPabx && (
              <>
                <div className="col-12 col-md-4 fw-semibold fs-6">
                  {intl.formatMessage({id: 'AUTH.INPUT.PASSWORD'})} *
                </div>
                <div className="col-12 col-md-8 d-flex align-items-center gap-3">
                  <div className="position-relative" style={{maxWidth: 160}}>
                    <Form.Control
                      type={motDePasseVisible ? 'text' : 'password'}
                      value={motDePasse}
                      onChange={(e) => setMotDePasse(e.target.value)}
                      style={{paddingRight: 32}}
                    />
                    <i
                      className={`bi ${motDePasseVisible ? 'bi-eye-slash' : 'bi-eye'} position-absolute cursor-pointer text-muted`}
                      style={{right: 10, top: '50%', transform: 'translateY(-50%)'}}
                      onClick={() => setMotDePasseVisible(!motDePasseVisible)}
                    />
                  </div>
                  <Button
                    label={intl.formatMessage({
                      id: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.GENERER',
                    })}
                    onClick={genererMotDePasse}
                  />
                </div>
                <div className="col-12 col-md-4 fw-semibold fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.COMPLEXITE'})}
                </div>
                <div className="col-12 col-md-8" style={{maxWidth: 220}}>
                  <ProgressBar
                    now={complexite}
                    variant={niveau.variantBarre}
                    label={`${complexite}%`}
                    style={{height: '20px'}}
                  />
                  <div className={`${niveau.classeTexte} fs-8 mt-1`}>
                    {intl.formatMessage({id: niveau.labelId})}
                  </div>
                </div>
              </>
            )}
          </div>

          {!isPabx && (
            <Alert variant="warning" className="d-flex align-items-center gap-3">
              <i className="bi bi-exclamation-triangle fs-3" />
              <span className="fs-7">
                {intl.formatMessage({
                  id: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.ALERT_MOT_DE_PASSE',
                })}
              </span>
            </Alert>
          )}

          <div className="row g-4 align-items-center mt-4">
            <div className="col-12 col-md-4 fw-semibold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.SERVEUR_SIP'})} *
            </div>
            <div className="col-12 col-md-8 fs-6">{identifiantsSip.serveurSip}</div>
            <div className="col-12 col-md-4 fw-semibold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.PROXY_SIP'})} *
            </div>
            <div className="col-12 col-md-8 fs-6">{identifiantsSip.proxySip}</div>
            <div className="col-12 col-md-4 fw-semibold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.LOCALISATION'})}
            </div>
            <div className="col-12 col-md-8">
              <Form.Select
                style={{maxWidth: 340}}
                value={localisationId ?? ''}
                onChange={(e) => setLocalisationId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value=""></option>
                {localisations.map((site) => (
                  <option key={site.id} value={site.id}>
                    {localisationEnLibelle(site)}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="col-12 col-md-4 fw-semibold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.LANGUE.LABEL'})}
            </div>
            <div className="col-12 col-md-8">
              <Form.Select
                style={{maxWidth: 160}}
                value={langue}
                onChange={(e) => setLangue(e.target.value)}
              >
                {LANGUES_VOICEMAIL.map(({code, labelId}) => (
                  <option key={code} value={code}>
                    {intl.formatMessage({id: labelId})}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
          <div className="text-muted fs-8 mt-4">
            {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES.MENTION_OBLIGATOIRE'})}
          </div>
        </Card.Body>
      </Card>
      <div className="text-center">
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CONFIRMER'})}
          onClick={() => onConfirm(motDePasse, localisationId, langue)}
        />
      </div>
    </div>
  );
};

/**
 * Numéro rattaché (non principal) : le compte SIP est celui du numéro principal, seule la
 * localisation est propre à ce numéro et modifiable ici.
 */
const ModifierParametresAvancesSimple = ({
  localisations,
  localisationIdActuelle,
  onConfirm,
}: {
  localisations: LocalisationSite[];
  localisationIdActuelle: number | null;
  onConfirm: (localisationId: number | null) => void;
}): JSX.Element => {
  const intl = useIntl();
  const [localisationId, setLocalisationId] = useState<number | null>(localisationIdActuelle);

  return (
    <div className="mt-4">
      <p className="text-muted fs-6 mb-5">
        {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.PARAMETRES_AVANCES_SIMPLE.INTRO'})}
      </p>
      <Card className="mb-5">
        <Card.Body>
          <div className="row g-4 align-items-center">
            <div className="col-12 col-md-4 fw-semibold fs-6 d-flex align-items-center gap-2">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.LOCALISATION'})}
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.LOCALISATION.TOOLTIP_INFO'})}
                    <br />
                    {intl.formatMessage(
                      {id: 'TELEPHONIE.NUMEROS.LOCALISATION.TOOLTIP_MENU'},
                      {menu: <em>{intl.formatMessage({id: 'TELEPHONIE.NUMEROS.LOCALISATION.TOOLTIP_MENU_LIEN'})}</em>}
                    )}
                  </Tooltip>
                }
              >
                <i className="bi bi-question-circle-fill text-primary fs-6 cursor-pointer" />
              </OverlayTrigger>
            </div>
            <div className="col-12 col-md-8">
              <Form.Select
                style={{maxWidth: 340}}
                value={localisationId ?? ''}
                onChange={(e) => setLocalisationId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value=""></option>
                {localisations.map((site) => (
                  <option key={site.id} value={site.id}>
                    {localisationEnLibelle(site)}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
        </Card.Body>
      </Card>
      <div className="text-center">
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CONFIRMER'})}
          onClick={() => onConfirm(localisationId)}
        />
      </div>
    </div>
  );
};

/**
 * Onglet « Gérer vos numéros » d'une prestation Trunk SIP.
 * Trois vues : liste des numéros, consultation, puis modification (reprise de la
 * maquette métier validée). La liste des numéros vient de l'API ; les vues
 * consultation/modification restent sur des champs par défaut tant que l'API ne les
 * porte pas (boîte vocale, appels entrants, limitations...).
 */
interface GererNumerosProps {
  serviceId: number;
  onConsulterConsommation?: (numero: string) => void;
  /** Bascule vers l'onglet « Restriction d'appels » (prestation), géré par le parent. */
  onModifierRestriction?: (numero: {numero: string; numeroId: number}) => void;
  isCentrex?: boolean;
  /**
   * `serviceName` contient PABX plutôt qu'IPBX — masque le mot de passe SIP
   * (pas de compte SIP exposé côté client sur un PABX).
   */
  isPabx?: boolean;
  isEssentielle?: boolean;
  isActive?: boolean;
}

const GererNumeros = ({
  serviceId,
  onConsulterConsommation,
  onModifierRestriction,
  isPabx = false,
  isCentrex = false,
  isEssentielle = false,
  isActive = true,
}: GererNumerosProps): JSX.Element | null => {
  const intl = useIntl();
  const [selected, setSelected] = useState<TrunkNumero | null>(null);
  /** Numéro dont le code de la boîte vocale est affiché dans la vue consultation (un seul à la fois). */
  const [numeroCodeBoiteVocaleVisible, setNumeroCodeBoiteVocaleVisible] = useState<number | null>(null);
  const [vue, setVue] = useState<Vue>('liste');
  const [ongletModifier, setOngletModifier] = useState<OngletModifier>('params-avances');
  const [ongletModifierGroupe, setOngletModifierGroupe] =
    useState<OngletModifierGroupe>('appels-entrants');
  const [selectionnes, setSelectionnes] = useState<Set<string>>(new Set());
  const {data, isLoading} = useGetTrunkNumeros(serviceId, isActive);
  const {data: numerosVirtuels} = useGetVirtualNumbers(serviceId);
  const {data: localisationsUrgence} = useGetLocalisationsUrgence(serviceId, isActive);
  const localisationsDisponibles = localisationsUrgence?.sites ?? [];
  const [numerosLocal, setNumerosLocal] = useState<TrunkNumero[] | null>(null);
  const [message, setMessage] = useAutoDismissMessage();
  const numeroDetail = useGetNumeroDetail();
  const mettreAJourNumeroApi = useMettreAJourNumero(serviceId);
  const mettreAJourNumeroGroupeApi = useMettreAJourNumero(serviceId, {invalidateOnSuccess: false});
  const queryClient = useQueryClient();
  const [chargementDetailId, setChargementDetailId] = useState<number | null>(null);

  /**
   * Résumé réel (pas la maquette `TrunkNumero.limitations`) des restrictions du numéro
   * consulté, pour la carte « Limitation / Restriction d'appels » : overrides sortants
   * (`authorization-overrides`), blacklist entrante filtrée sur ce numéro et filtrage IP
   * (propre au numéro, ou hérité du service). Requêtes désactivées hors vue « Consulter »
   * pour ne pas les déclencher pour chaque numéro de la liste.
   */
  const consultationActive = vue === 'consulter' && selected !== null;
  const numeroIdConsulte = selected?.numeroId ?? 0;
  const {data: overridesConsultes} = useGetAuthorizationOverrides(
    serviceId,
    numeroIdConsulte,
    consultationActive
  );
  const {data: blacklistEntrants} = useGetBlacklistEntrants(serviceId, consultationActive);
  const {data: ipRestrictionService} = useGetIpRestriction(serviceId, consultationActive);
  const {data: ipRestrictionNumero} = useGetNumberIpRestriction(
    serviceId,
    numeroIdConsulte,
    consultationActive
  );
  const limitationRows = useMemo<InfoRow[]>(() => {
    const refus = overridesConsultes?.authorizations.filter((a) => a.override === 'deny') ?? [];
    const entrantsBloques =
      blacklistEntrants?.filter((entree) => entree.numeroAppele === selected?.numero).length ?? 0;
    const zoneEffective = ipRestrictionNumero?.inherited
      ? ipRestrictionService?.zone
      : ipRestrictionNumero?.zone;
    const exceptionsEffectives =
      (ipRestrictionNumero?.inherited
        ? ipRestrictionService?.ipExceptions
        : ipRestrictionNumero?.ipExceptions) ?? [];
    const filtrageIpActif = !!zoneEffective && zoneEffective !== 'world';

    return [
      {
        label: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TAB_SORTANTS'}),
        value:
          refus.length > 0
            ? refus.map((a) => a.label).join(', ')
            : intl.formatMessage({id: 'TELEPHONIE.NUMEROS.LIMITATION.AUCUNE'}),
        danger: refus.length > 0,
      },
      {
        label: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TAB_ENTRANTS'}),
        value:
          entrantsBloques > 0
            ? intl.formatMessage(
                {id: 'TELEPHONIE.NUMEROS.LIMITATION.ENTRANTS_BLOQUES'},
                {count: entrantsBloques}
              )
            : intl.formatMessage({id: 'TELEPHONIE.NUMEROS.LIMITATION.AUCUNE'}),
        danger: entrantsBloques > 0,
      },
      {
        label: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TAB_IP'}),
        value:
          filtrageIpActif && zoneEffective
            ? `${intl.formatMessage({id: ZONE_LABEL_ID[zoneEffective]})}${
                exceptionsEffectives.length > 0 ? ` (${exceptionsEffectives.join(', ')})` : ''
              }`
            : intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CHAMP.NON'}),
        danger: !filtrageIpActif,
      },
    ];
  }, [
    overridesConsultes,
    blacklistEntrants,
    ipRestrictionService,
    ipRestrictionNumero,
    selected,
    intl,
  ]);

  useEffect(() => {
    if (vue === 'modifier' || vue === 'modifier-groupe') {
      setMessage(null);
    }
  }, [vue, setMessage]);

  useEffect(() => {
    if (data) {
      setNumerosLocal(data.numeros);
    }
  }, [data]);

  const numeros = useMemo(
    () => [...(numerosLocal ?? data?.numeros ?? [])].sort(parOrdreAffichage),
    [numerosLocal, data]
  );

  const basculerSelection = useCallback((numero: string): void => {
    setSelectionnes((precedent) => {
      const suivant = new Set(precedent);
      if (suivant.has(numero)) {
        suivant.delete(numero);
      } else {
        suivant.add(numero);
      }
      return suivant;
    });
  }, []);

  const numerosSelectionnes = useMemo(
    () => numeros.filter((numero) => selectionnes.has(numero.numero)),
    [numeros, selectionnes]
  );

  const buildExportTable = (): {header: string[]; rows: string[][]} => {
    // Sans sélection, on exporte tout le tableau plutôt que de bloquer l'export.
    const numerosAExporter = numerosSelectionnes.length > 0 ? numerosSelectionnes : numeros;
    const header = [
      intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CHAMP.NUMERO'}),
      intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CHAMP.ATTRIBUE_A'}),
      intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CHAMP.PRINCIPAL'}),
      intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CHAMP.SITE'}),
    ];
    const rows = numerosAExporter.map((numero) => [
      numero.numero,
      numero.attribueA,
      numero.principal
        ? intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CHAMP.OUI'})
        : intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CHAMP.NON'}),
      numero.site.join(' '),
    ]);
    return {header, rows};
  };

  const handleExportNumerosCsv = (): void => {
    const {header, rows} = buildExportTable();
    const csv = [header, ...rows].map((row) => row.join(';')).join('\n');
    const blob = new Blob([`﻿${csv}`], {type: 'text/csv;charset=utf-8'});
    saveAs(blob, 'numeros-selectionnes.csv');
  };

  const handleExportNumerosExcel = (): void => {
    const {header, rows} = buildExportTable();
    // mso-number-format:'@' force le format Texte de la cellule, sinon Excel réinterprète un
    // « + » en tête de valeur comme une formule et l'affiche sans son signe.
    const excelCell = (value: string): string =>
      value.startsWith('+') ? `<td style="mso-number-format:'@'">${value}</td>` : `<td>${value}</td>`;
    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="utf-8" /></head>
        <body>
          <table border="1">
            <tr>${header.map((cell) => `<th>${cell}</th>`).join('')}</tr>
            ${rows.map((row) => `<tr>${row.map(excelCell).join('')}</tr>`).join('')}
          </table>
        </body>
      </html>`;
    const blob = new Blob([html], {type: 'application/vnd.ms-excel;charset=utf-8'});
    saveAs(blob, 'numeros-selectionnes.xls');
  };

  const handleExportNumerosPdf = (): void => {
    const {header, rows} = buildExportTable();
    // Format paysage + largeurs de colonnes proportionnelles avec retour à la ligne
    // (splitTextToSize) : en largeur fixe, un « Site » (adresse complète) déborde de
    // sa colonne et sort de la page.
    const doc = new jsPDF({orientation: 'landscape', unit: 'mm', format: 'a4'});
    const marginX = 10;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usableWidth = pageWidth - marginX * 2;
    // Numéro, Attribué à, Principal, Site : ce dernier (adresse complète) a besoin de
    // loin le plus de place, « Principal » (Oui/Non) le moins.
    const colWidths = [0.15, 0.25, 0.1, 0.5].map((weight) => usableWidth * weight);
    const lineHeight = 4;
    let y = 15;

    doc.setFontSize(10);

    const printRow = (row: string[], bold: boolean): void => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      const cellLines = row.map((cell, index) => doc.splitTextToSize(cell, colWidths[index] - 2));
      const rowHeight = Math.max(...cellLines.map((lines) => lines.length)) * lineHeight + 2;
      if (y + rowHeight > pageHeight - 10) {
        doc.addPage();
        y = 15;
      }
      let x = marginX;
      cellLines.forEach((lines, index) => {
        doc.text(lines, x, y);
        x += colWidths[index];
      });
      y += rowHeight;
    };

    printRow(header, true);
    rows.forEach((row) => printRow(row, false));

    doc.save('numeros-selectionnes.pdf');
  };

  /**
   * Charge toujours le détail complet (sipAccount, location.locationId,
   * voicemail...) avant d'agir : la ligne de liste seule ne les porte pas.
   * Si `numero` a déjà été enrichi par un chargement précédent (ex. on vient
   * de « Consulter »), pas besoin de refaire l'appel.
   */
  const chargerDetailPuis = useCallback(
    (numero: TrunkNumero, suite: (detail: TrunkNumero) => void): void => {
      if (numero.detail) {
        suite(numero);
        return;
      }

      setMessage(null);
      setChargementDetailId(numero.numeroId);
      numeroDetail.mutate(
        {serviceId, numeroId: numero.numeroId},
        {
          onSuccess: (detail) => {
            setChargementDetailId(null);
            suite(detail);
          },
          onError: () => {
            setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ERREUR_CHARGEMENT_DETAIL'}));
            setChargementDetailId(null);
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceId, intl]
  );

  const ouvrirConsulter = useCallback(
    (numero: TrunkNumero): void => {
      chargerDetailPuis(numero, (detail) => {
        setSelected(detail);
        setVue('consulter');
      });
    },
    [chargerDetailPuis]
  );

  const ouvrirModifier = useCallback(
    (numero: TrunkNumero, onglet: OngletModifier): void => {
      chargerDetailPuis(numero, (detail) => {
        setSelected(detail);
        setOngletModifier(onglet);
        setVue('modifier');
      });
    },
    [chargerDetailPuis]
  );

  /**
   * N'imprime que les paramètres du numéro (`.numero-detail-print-area`), en masquant
   * temporairement le reste de la page (menus, en-tête, tableau « Autres numéros »...).
   */
  const handleImprimerParamsAvances = useCallback((): void => {
    document.body.classList.add('numero-detail-print');
    window.addEventListener(
      'afterprint',
      () => document.body.classList.remove('numero-detail-print'),
      {once: true}
    );
    window.print();
  }, []);

  const columns = useMemo<ColumnDef<TrunkNumero>[]>(
    () => [
      {
        header: () => (
          <Form.Check
            type="checkbox"
            onClick={(e) => e.stopPropagation()}
            checked={
              numeros.length > 0 &&
              numeros.every((numero) => selectionnes.has(numero.numero))
            }
            onChange={(e) => {
              setSelectionnes(
                e.target.checked ? new Set(numeros.map((n) => n.numero)) : new Set()
              );
            }}
          />
        ),
        id: 'select',
        size: 40,
        enableSorting: false,
        cell: (info) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Form.Check
              type="checkbox"
              checked={selectionnes.has(info.row.original.numero)}
              onChange={() => basculerSelection(info.row.original.numero)}
            />
          </div>
        ),
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CHAMP.NUMERO'}),
        accessorKey: 'numero',
        size: 260,
        cell: (info) => {
          const numero = info.row.original;
          const type = typeDeNumero(numero);
          const icone = type ? ICONE_TYPE_NUMERO[type] : null;

          return (
            <div className="d-flex align-items-center gap-3">
              {icone ? (
                <OverlayTrigger
                  overlay={<Tooltip>{intl.formatMessage({id: icone.labelId})}</Tooltip>}
                >
                  <i className={`bi ${icone.classe} fs-5`} />
                </OverlayTrigger>
              ) : (
                <div style={{width: 20, height: 1, background: '#a1a5b7', marginLeft: 4}} />
              )}
              {numero.principal ? (
                <ConnexionDot
                  color={numero.actif ? COULEUR_ACTIF : COULEUR_INACTIF}
                  label={numero.numero}
                  subLabel={numero.attribueA || null}
                />
              ) : (
                <span className="fw-semibold fs-7">{numero.numero}</span>
              )}
            </div>
          );
        },
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CHAMP.SITE'}),
        accessorKey: 'site',
        cell: (info) => (
          <div className="text-muted fs-7">
            {info.row.original.site.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        ),
      },
      {
        header: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CHAMP.ACTIONS'}),
        id: 'actions',
        size: 140,
        enableSorting: false,
        cell: (info) => {
          if (info.row.original.reserve) {
            return null;
          }

          return chargementDetailId === info.row.original.numeroId ? (
            <span className="spinner-border spinner-border-sm text-primary" style={{margin: 5}} />
          ) : (
            <div onClick={(e) => e.stopPropagation()}>
              <ActionIcon
                label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ACTION.CONSULTER'})}
                faIcon="fs-3 bi bi-search"
                onClick={() => ouvrirConsulter(info.row.original)}
              />
              <ActionIcon
                label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ACTION.CONSOMMATION'})}
                faIcon="fs-3 bi bi-file-earmark-text"
                onClick={() => onConsulterConsommation?.(info.row.original.numero)}
              />
              <ActionIcon
                label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ACTION.MODIFIER'})}
                faIcon="fs-3 bi bi-pencil"
                onClick={() => ouvrirModifier(info.row.original, 'params-avances')}
              />
            </div>
          );
        },
      },
    ],
    [
      onConsulterConsommation,
      numeros,
      selectionnes,
      intl,
      chargementDetailId,
      basculerSelection,
      ouvrirConsulter,
      ouvrirModifier,
    ]
  );

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  if (vue === 'modifier-groupe') {
    const terminer = (): void => {
      setVue('liste');
      setSelectionnes(new Set());
      setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.MODIFICATION_EFFECTUEE'}));
    };

    const modifierGroupeTabs = [
      ...(isCentrex
        ? [
            {
              title: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.TAB.BOITE_VOCALE'}),
              key: 'boite-vocale',
              content: (
                <div className="mt-4">
                  <Card>
                    <Card.Body className="text-muted fs-6">
                      {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE_INDISPONIBLE'})}
                    </Card.Body>
                  </Card>
                </div>
              ),
            },
          ]
        : []),
      {
        title: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.TAB.APPELS_ENTRANTS'}),
        key: 'appels-entrants',
        content: (
          <ModifierAppelsEntrants
            appelsEntrants={numerosSelectionnes[0]?.appelsEntrants ?? []}
            parametres={numerosSelectionnes[0]?.detail?.inboundCalls}
            isSubmitting={mettreAJourNumeroGroupeApi.isLoading}
            onConfirm={async (valeurs) => {
              const payload = {inboundCalls: appelsEntrantsEnInboundCallsPayload(valeurs)};
              try {
                for (const numero of numerosSelectionnes) {
                  // eslint-disable-next-line no-await-in-loop
                  await mettreAJourNumeroGroupeApi.mutateAsync({
                    numeroId: numero.numeroId,
                    payload,
                  });
                }
                terminer();
              } catch {
                setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ERREUR_MODIFICATION'}));
              } finally {
                await queryClient.invalidateQueries(['trunk-sip-numeros', serviceId]);
              }
            }}
          />
        ),
      },
      {
        title: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.TAB.APPELS_SORTANTS'}),
        key: 'appels-sortants',
        content: (
          <ModifierAppelsSortants
            autresNumeros={[
              ...numeros
                .filter((numero) => !selectionnes.has(numero.numero))
                .map((numero) => numero.numero),
              ...(numerosVirtuels ?? []),
            ]}
            valeurInitiale={numerosSelectionnes[0]?.numeroAffiche ?? NUMERO_COURANT}
            isSubmitting={mettreAJourNumeroGroupeApi.isLoading}
            onConfirm={async (numeroAffiche) => {
              const payload = {outboundCalls: numeroAfficheEnOutboundCallsPayload(numeroAffiche)};
              try {
                for (const numero of numerosSelectionnes) {
                  // eslint-disable-next-line no-await-in-loop
                  await mettreAJourNumeroGroupeApi.mutateAsync({
                    numeroId: numero.numeroId,
                    payload,
                  });
                }
                terminer();
              } catch {
                setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ERREUR_MODIFICATION'}));
              } finally {
                await queryClient.invalidateQueries(['trunk-sip-numeros', serviceId]);
              }
            }}
          />
        ),
      },
    ];

    return (
      <div className="mt-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div className="d-flex align-items-center gap-2 fs-6">
            <span className="text-primary cursor-pointer" onClick={() => setVue('liste')}>
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BREADCRUMB.RACINE'})}
            </span>
            <i className="bi bi-chevron-right fs-8" />
            <span className="fw-semibold">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BREADCRUMB.MODIFICATION_GROUPEE'})}
            </span>
          </div>
          <Button
            label={intl.formatMessage({id: 'COMMON.BACK'})}
            faIcon="bi bi-arrow-left fs-6 me-2"
            onClick={() => setVue('liste')}
          />
        </div>

        {message && <div className="fw-bold text-danger mb-4">{message}</div>}

        <p className="text-muted fs-6 mb-4">
          {intl.formatMessage(
            {id: 'TELEPHONIE.NUMEROS.GROUPE.INTRO'},
            {count: numerosSelectionnes.length}
          )}
        </p>
        <div className="fw-bold fs-6 mb-4">
          {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.GROUPE.TITRE'})} —{' '}
          {numerosSelectionnes.map((numero) => numero.numero).join(', ')}
        </div>
        <Alert variant="info" className="d-flex align-items-center gap-3 mb-4">
          <i className="bi bi-info-circle fs-3" />
          <span className="fs-7">{intl.formatMessage({id: 'TELEPHONIE.NUMEROS.GROUPE.ALERT_SIP'})}</span>
        </Alert>

        <TabComponent
          tabData={modifierGroupeTabs}
          activeKey={ongletModifierGroupe}
          onTabChange={(key) => setOngletModifierGroupe(key as OngletModifierGroupe)}
        />
      </div>
    );
  }

  if (selected && vue === 'modifier') {
    const modifierTabs = [
      ...(isCentrex || isEssentielle
        ? [
            {
              title: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.TAB.BOITE_VOCALE'}),
              key: 'boite-vocale',
              content: isEssentielle ? (
                <ModifierBoiteVocale
                  serviceId={serviceId}
                  numeroId={selected.numeroId}
                  voicemail={selected.detail?.voicemail ?? null}
                  onConfirm={(misAJour) => {
                    setSelected(misAJour);
                    setVue('consulter');
                    setMessage(
                      intl.formatMessage({id: 'TELEPHONIE.NUMEROS.MODIFICATION_EFFECTUEE'})
                    );
                  }}
                />
              ) : (
                <div className="mt-4">
                  <Card>
                    <Card.Body className="text-muted fs-6">
                      {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BOITE_VOCALE_INDISPONIBLE'})}
                    </Card.Body>
                  </Card>
                </div>
              ),
            },
          ]
        : []),
      {
        title: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.TAB.APPELS_ENTRANTS'}),
        key: 'appels-entrants',
        content: (
          <ModifierAppelsEntrants
            appelsEntrants={selected.appelsEntrants}
            voicemailActive={selected.detail?.voicemail?.enabled ?? false}
            noAnswerTimeout={selected.detail?.inboundCalls?.noAnswerTimeout ?? undefined}
            parametres={selected.detail?.inboundCalls}
            isSubmitting={mettreAJourNumeroApi.isLoading}
            onConfirm={(valeurs) => {
              mettreAJourNumeroApi.mutate(
                {
                  numeroId: selected.numeroId,
                  payload: {inboundCalls: appelsEntrantsEnInboundCallsPayload(valeurs)},
                },
                {
                  onSuccess: (misAJour) => {
                    setSelected(misAJour);
                    setVue('consulter');
                    setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.MODIFICATION_EFFECTUEE'}));
                  },
                  onError: () => {
                    setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ERREUR_MODIFICATION'}));
                  },
                }
              );
            }}
          />
        ),
      },
      {
        title: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.TAB.APPELS_SORTANTS'}),
        key: 'appels-sortants',
        content: (
          <ModifierAppelsSortants
            autresNumeros={[
              ...numeros
                .filter((numero) => numero.numero !== selected.numero)
                .map((numero) => numero.numero),
              ...(numerosVirtuels ?? []),
            ]}
            valeurInitiale={selected.numeroAffiche ?? NUMERO_COURANT}
            isSubmitting={mettreAJourNumeroApi.isLoading}
            onConfirm={(numeroAffiche) => {
              mettreAJourNumeroApi.mutate(
                {
                  numeroId: selected.numeroId,
                  payload: {outboundCalls: numeroAfficheEnOutboundCallsPayload(numeroAffiche)},
                },
                {
                  onSuccess: (misAJour) => {
                    setSelected(misAJour);
                    setVue('consulter');
                    setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.MODIFICATION_EFFECTUEE'}));
                  },
                  onError: () => {
                    setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ERREUR_MODIFICATION'}));
                  },
                }
              );
            }}
          />
        ),
      },
      {
        title: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.TAB.PARAMS_AVANCES'}),
        key: 'params-avances',
        content: selected.principal ? (
          <ModifierParametresAvances
            serviceId={serviceId}
            numeroId={selected.numeroId}
            identifiantsSip={selected.identifiantsSip}
            localisations={localisationsDisponibles}
            localisationIdActuelle={selected.detail?.location?.locationId ?? null}
            langueActuelle={selected.detail?.voicemail?.language ?? 'fr'}
            isPabx={isPabx}
            onConfirm={(motDePasse, localisationId, langue) => {
              mettreAJourNumeroApi.mutate(
                {
                  numeroId: selected.numeroId,
                  payload: {
                    ...(localisationId !== null ? {locationId: localisationId} : {}),
                    ...(motDePasse ? {sipAccount: {password: motDePasse}} : {}),
                    voicemail: {
                      ...voicemailInchange(selected.detail?.voicemail ?? null),
                      language: langue,
                    },
                  },
                },
                {
                  onSuccess: (misAJour) => {
                    setSelected(misAJour);
                    setVue('consulter');
                    setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.MODIFICATION_EFFECTUEE'}));
                  },
                  onError: () => {
                    setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ERREUR_MODIFICATION'}));
                  },
                }
              );
            }}
          />
        ) : (
          <ModifierParametresAvancesSimple
            localisations={localisationsDisponibles}
            localisationIdActuelle={selected.detail?.location?.locationId ?? null}
            onConfirm={(localisationId) => {
              mettreAJourNumeroApi.mutate(
                {
                  numeroId: selected.numeroId,
                  payload: localisationId !== null ? {locationId: localisationId} : {},
                },
                {
                  onSuccess: (misAJour) => {
                    setSelected(misAJour);
                    setVue('consulter');
                    setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.MODIFICATION_EFFECTUEE'}));
                  },
                  onError: () => {
                    setMessage(intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ERREUR_MODIFICATION'}));
                  },
                }
              );
            }}
          />
        ),
      },
    ];

    return (
      <div className="mt-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
          <div className="d-flex align-items-center gap-2 fs-6">
            <span
              className="text-primary cursor-pointer"
              onClick={() => {
                setVue('liste');
                setSelected(null);
              }}
            >
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BREADCRUMB.RACINE'})}
            </span>
            <i className="bi bi-chevron-right fs-8" />
            <span className="text-primary cursor-pointer" onClick={() => setVue('consulter')}>
              {selected.numero}
            </span>
            <i className="bi bi-chevron-right fs-8" />
            <span className="fw-semibold">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BREADCRUMB.MODIFICATION'})}
            </span>
          </div>
          <Button
            label={intl.formatMessage({id: 'COMMON.BACK'})}
            faIcon="bi bi-arrow-left fs-6 me-2"
            onClick={() => setVue('consulter')}
          />
        </div>

        <p className="text-muted fs-6 mb-4">
          {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.MODIFICATION.INTRO'})}
        </p>
        <div className="fw-bold fs-6 mb-4">
          {intl.formatMessage(
            {id: 'TELEPHONIE.NUMEROS.MODIFICATION.TITRE'},
            {numero: selected.numero}
          )}
        </div>

        <TabComponent
          tabData={modifierTabs}
          activeKey={ongletModifier}
          onTabChange={(key) => setOngletModifier(key as OngletModifier)}
        />
      </div>
    );
  }

  if (selected && vue === 'consulter') {
    return (
      <div className="mt-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-5">
          <div className="d-flex align-items-center gap-2 fs-6">
            <span
              className="text-primary cursor-pointer"
              onClick={() => {
                setVue('liste');
                setSelected(null);
              }}
            >
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.BREADCRUMB.RACINE'})}
            </span>
            <i className="bi bi-chevron-right fs-8" />
            <span className="fw-semibold">{selected.numero}</span>
          </div>
          <Button
            label={intl.formatMessage({id: 'COMMON.BACK'})}
            faIcon="bi bi-arrow-left fs-6 me-2"
            onClick={() => {
              setVue('liste');
              setSelected(null);
            }}
          />
        </div>

        {message && <div className="fw-bold text-danger mb-4">{message}</div>}

        <Card className="mb-5">
          <Card.Body className="d-flex flex-wrap align-items-center justify-content-between gap-4">
            <ConnexionDot
              color={selected.actif ? COULEUR_ACTIF : COULEUR_INACTIF}
              label={selected.numero}
              subLabel={selected.attribueA || null}
            />
            <div className="text-muted fs-7 text-end">
              {selected.site.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </div>
          </Card.Body>
        </Card>

        <div className="numero-detail-print-area">
          <InfoCard
            title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.TAB.PARAMS_AVANCES'})}
            printAction={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ACTION.IMPRIMER'})}
            onPrintClick={handleImprimerParamsAvances}
            action={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ACTION.MODIFIER'})}
            onActionClick={() => ouvrirModifier(selected, 'params-avances')}
            rows={
              selected.lie
                ? [
                    {
                      label: 'Numéro principal',
                      value: numeros.find((n) => n.principal)?.numero ?? '',
                    },
                    {label: 'Numéro', value: selected.numero},
                  ]
                : [
                    {label: 'Numéro', value: selected.numero},
                    {
                      label: 'Attribué à',
                      value: selected.attribueA || "Ce numéro n'est pas attribué",
                    },
                  ]
            }
          />
          <InfoCard
            title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.IDENTIFIANTS_SIP.TITRE'})}
            rows={identifiantsSipEnLignes(
              // Un numéro lié n'a pas de compte SIP propre : celui-ci est celui du numéro
              // principal du trunk (cf. ModifierParametresAvancesSimple ci-dessus).
              (selected.lie && numeros.find((n) => n.principal)?.identifiantsSip) ||
                selected.identifiantsSip,
              intl,
              () => ouvrirModifier(selected, 'params-avances'),
              isPabx
            )}
          />
          {(isCentrex || isEssentielle) && (
            <InfoCard
              title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.TAB.BOITE_VOCALE'})}
              action={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ACTION.MODIFIER'})}
              onActionClick={() => ouvrirModifier(selected, 'boite-vocale')}
              rows={boiteVocaleEnLignes(
                selected,
                isEssentielle,
                numeroCodeBoiteVocaleVisible === selected.numeroId,
                () => setNumeroCodeBoiteVocaleVisible(selected.numeroId)
              )}
            />
          )}
          <InfoCard
            title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.TAB.APPELS_ENTRANTS'})}
            action={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ACTION.MODIFIER'})}
            onActionClick={() => ouvrirModifier(selected, 'appels-entrants')}
            rows={selected.appelsEntrants}
          />
          <InfoCard
            title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.TAB.APPELS_SORTANTS'})}
            action={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ACTION.MODIFIER'})}
            onActionClick={() => ouvrirModifier(selected, 'appels-sortants')}
            rows={[
              {
                label: intl.formatMessage({id: 'TELEPHONIE.NUMEROS.CHAMP.NUMERO_AFFICHE'}),
                value: numeroAfficheLabel(selected, intl),
              },
            ]}
          />
          <InfoCard
            title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.LIMITATION.TITRE'})}
            action={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ACTION.MODIFIER'})}
            onActionClick={() =>
              onModifierRestriction?.({numero: selected.numero, numeroId: selected.numeroId})
            }
            rows={limitationRows}
          />
        </div>

        <TableMetronic<TrunkNumero>
          title={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.AUTRES_NUMEROS.TITRE'})}
          data={numeros.filter((numero) => numero.numero !== selected.numero)}
          columns={columns}
          pagination
          elementName="numéro"
          elementNamePlural="numéros"
          rowClickable={(row) => !row.reserve}
          onRowClick={(row) => ouvrirConsulter(row)}
        />
      </div>
    );
  }

  return (
    <div className="mt-4">
      {message && <div className="fw-bold text-danger mb-3">{message}</div>}
      {selectionnes.size > 0 && (
        <Alert
          variant="light"
          className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4"
        >
          <span className="fs-6 fw-semibold">
            {intl.formatMessage(
              {id: 'TELEPHONIE.NUMEROS.SELECTION.COMPTEUR'},
              {count: selectionnes.size}
            )}
          </span>
          <div className="d-flex flex-wrap gap-3">
            <Button
              label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.SELECTION.DESELECTIONNER_TOUT'})}
              onClick={() => setSelectionnes(new Set())}
            />
            <Button
              label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.SELECTION.MODIFIER'})}
              faIcon="bi bi-pencil fs-6 me-2"
              onClick={() => {
                setOngletModifierGroupe('appels-entrants');
                setVue('modifier-groupe');
              }}
            />
          </div>
        </Alert>
      )}

      <TableMetronic<TrunkNumero>
        title={intl.formatMessage(
          {id: 'TELEPHONIE.NUMEROS.LISTE.TITRE'},
          {count: numeros.length}
        )}
        data={numeros}
        columns={columns}
        pagination
        elementName={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ELEMENT_NAME'})}
        elementNamePlural={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.ELEMENT_NAME_PLURAL'})}
        subtitle={
          <div className="d-flex flex-wrap align-items-center justify-content-between w-100 gap-3">
            <span className="text-muted fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.NUMEROS.LISTE.SOUS_TITRE'})}{' '}
              <strong>{data.prestation}</strong>
            </span>
            <div className="d-flex flex-wrap align-items-center gap-8">
              <ExportLink
                icon="bi-filetype-csv"
                label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.SELECTION.EXPORTER_CSV'})}
                onClick={handleExportNumerosCsv}
              />
              <ExportLink
                icon="bi-filetype-xls"
                label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.SELECTION.EXPORTER_EXCEL'})}
                onClick={handleExportNumerosExcel}
              />
              <ExportLink
                icon="bi-filetype-pdf"
                label={intl.formatMessage({id: 'TELEPHONIE.NUMEROS.SELECTION.EXPORTER_PDF'})}
                onClick={handleExportNumerosPdf}
              />
            </div>
          </div>
        }
        rowClickable={(row) => !row.reserve}
        onRowClick={(row) => ouvrirConsulter(row)}
      />
    </div>
  );
};

export default GererNumeros;
